<script lang="ts" setup>
import {
  instanceOfNumericDataSetFilterDto,
  instanceOfEnumerationValueListDto,
  instanceOfTextValueListDto,
} from '@/api/generator';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { I18nKey } from '@/utils/typings.ts';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import { useDataSetFiltering } from '@/views/TrendsSelectionView/useDataSetFiltering.ts';
import { v7 as uuidv7 } from 'uuid';

defineEmits<{
  filtersChanged: [noFilterSelected: boolean];
}>();

const {
  filterMenuSelections,
  filters,
  deselectTaskType,
  deselectSupportSystem,
  deselectTargetSystem,
  deselectEvaluationParameter,
  toggleParameterOption,
  hasActiveFilters,
} = useDataSetFiltering();
</script>

<template>
  <div id="active-filters">
    <!-- Task Types -->
    <template
      v-for="(taskType, index) in filterMenuSelections.taskTypes"
      :key="`${index}-${taskType}`"
    >
      <v-chip
        closable
        @click:close="
          () => {
            deselectTaskType(taskType);
            $emit('filtersChanged', !hasActiveFilters);
          }
        "
        ><b>{{ $t(`TaskTypeDescriptionEnum.${taskType}`) }}</b>
      </v-chip>
    </template>

    <!-- Support Systems -->
    <template
      v-for="supportSystemId in filterMenuSelections.supportSystems"
      :key="`selected-support-system-${supportSystemId}`"
    >
      <v-chip
        closable
        @click:close="
          () => {
            deselectSupportSystem(supportSystemId);
            $emit('filtersChanged', !hasActiveFilters);
          }
        "
      >
        <IdentifierLabel
          :identifier="filters.availableSupportSystems.find((s) => s.id === supportSystemId)?.iuid"
          :label="filters.availableSupportSystems.find((s) => s.id === supportSystemId)?.name"
        />
      </v-chip>
    </template>

    <!-- Target Systems -->
    <template
      v-for="targetSystemId in filterMenuSelections.targetSystems"
      :key="`selected-target-system-${targetSystemId}`"
    >
      <v-chip
        closable
        @click:close="
          () => {
            deselectTargetSystem(targetSystemId);
            $emit('filtersChanged', !hasActiveFilters);
          }
        "
      >
        <IdentifierLabel
          :identifier="filters.availableTargetSystems.find((s) => s.id === targetSystemId)?.iuid"
          :label="filters.availableTargetSystems.find((s) => s.id === targetSystemId)?.name"
        />
      </v-chip>
    </template>

    <!-- Target System Parameters -->
    <template
      v-for="selectedTargetSystemParameter in filters.targetSystemSetupFilters.filter((filter) => {
        if (instanceOfEnumerationValueListDto(filter)) {
          return filter.selectedOptions?.length;
        } else if (instanceOfNumericDataSetFilterDto(filter)) {
          return filter.selectedValues?.length;
        } else if (instanceOfTextValueListDto(filter)) {
          return filter.selectedValues?.length;
        } else return false;
      })"
      :key="`selected-target-system-parameter-${selectedTargetSystemParameter.iuid}-${uuidv7()}`"
    >
      <template v-if="instanceOfEnumerationValueListDto(selectedTargetSystemParameter)">
        <template
          v-for="option in selectedTargetSystemParameter.selectedOptions"
          :key="`selected-target-system-parameter-option-${option.id}`"
        >
          <v-chip
            v-if="typeof option.id === 'number'"
            closable
            @click:close="
              () => {
                if (option.id === null) return;

                toggleParameterOption(selectedTargetSystemParameter, option);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedTargetSystemParameter.iuid"
              :label="getSetupParameterName(selectedTargetSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ $t(option.name as I18nKey) }}</div>
          </v-chip>
        </template>
      </template>
      <template v-else-if="instanceOfNumericDataSetFilterDto(selectedTargetSystemParameter)">
        <template
          v-for="value in selectedTargetSystemParameter.selectedValues"
          :key="`selected-target-system-parameter-value-${value.value}-${value.unitAbbreviation}`"
        >
          <v-chip
            closable
            @click:close="
              () => {
                toggleParameterOption(selectedTargetSystemParameter, value);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedTargetSystemParameter.iuid"
              :label="getSetupParameterName(selectedTargetSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ value.value }} {{ value.unitAbbreviation }}</div>
          </v-chip>
        </template>
      </template>
      <template v-else-if="instanceOfTextValueListDto(selectedTargetSystemParameter)">
        <template
          v-for="value in selectedTargetSystemParameter.selectedValues"
          :key="`selected-target-system-parameter-value-${value}`"
        >
          <v-chip
            closable
            @click:close="
              () => {
                toggleParameterOption(selectedTargetSystemParameter, value);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedTargetSystemParameter.iuid"
              :label="getSetupParameterName(selectedTargetSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ value }}</div>
          </v-chip>
        </template>
      </template>
    </template>

    <!-- Support System Parameters -->
    <template
      v-for="selectedSupportSystemParameter in filters.supportSystemSetupFilters.filter(
        (filter) => {
          if (instanceOfEnumerationValueListDto(filter)) {
            return filter.selectedOptions?.length;
          } else if (instanceOfNumericDataSetFilterDto(filter)) {
            return filter.selectedValues?.length;
          } else if (instanceOfTextValueListDto(filter)) {
            return filter.selectedValues?.length;
          } else return false;
        },
      )"
      :key="`selected-support-system-parameter-${selectedSupportSystemParameter.iuid}-${uuidv7()}`"
    >
      <template v-if="instanceOfEnumerationValueListDto(selectedSupportSystemParameter)">
        <template
          v-for="option in selectedSupportSystemParameter.selectedOptions"
          :key="`selected-support-system-parameter-option-${option.id}`"
        >
          <v-chip
            v-if="typeof option.id === 'number'"
            closable
            @click:close="
              () => {
                if (option.id === null) return;

                toggleParameterOption(selectedSupportSystemParameter, option);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedSupportSystemParameter.iuid"
              :label="getSetupParameterName(selectedSupportSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ $t(option.name as I18nKey) }}</div>
          </v-chip>
        </template>
      </template>
      <template v-else-if="instanceOfNumericDataSetFilterDto(selectedSupportSystemParameter)">
        <template
          v-for="value in selectedSupportSystemParameter.selectedValues"
          :key="`selected-support-system-parameter-value-${value.value}-${value.unitAbbreviation}`"
        >
          <v-chip
            closable
            @click:close="
              () => {
                toggleParameterOption(selectedSupportSystemParameter, value);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedSupportSystemParameter.iuid"
              :label="getSetupParameterName(selectedSupportSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ value.value }} {{ value.unitAbbreviation }}</div>
          </v-chip>
        </template>
      </template>
      <template v-else-if="instanceOfTextValueListDto(selectedSupportSystemParameter)">
        <template
          v-for="value in selectedSupportSystemParameter.selectedValues"
          :key="`selected-support-system-parameter-value-${value}`"
        >
          <v-chip
            closable
            @click:close="
              () => {
                toggleParameterOption(selectedSupportSystemParameter, value);
                $emit('filtersChanged', !hasActiveFilters);
              }
            "
          >
            <IdentifierLabel
              :identifier="selectedSupportSystemParameter.iuid"
              :label="getSetupParameterName(selectedSupportSystemParameter)"
            />&nbsp;|&nbsp;
            <div class="option-value">{{ value }}</div>
          </v-chip>
        </template>
      </template>
    </template>

    <!-- Evaluations -->
    <template
      v-for="evaluationParameterId in filterMenuSelections.evaluationParameters"
      :key="`selected-evaluation-parameter-${evaluationParameterId}`"
    >
      <v-chip
        closable
        @click:close="
          () => {
            deselectEvaluationParameter(evaluationParameterId);
            $emit('filtersChanged', !hasActiveFilters);
          }
        "
      >
        <IdentifierLabel
          :identifier="
            filters.evaluationParameterFilters.find((e) => e.id === evaluationParameterId)?.iuid
          "
          :label="
            getSetupParameterName(
              filters.evaluationParameterFilters.find((e) => e.id === evaluationParameterId),
            )
          "
        />
      </v-chip>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/vuetifySettings';

#active-filters {
  margin-top: vuetifySettings.$spacer * 6;
  margin-bottom: vuetifySettings.$spacer * 6;
  display: flex;
  flex-wrap: wrap;
  gap: vuetifySettings.$spacer vuetifySettings.$spacer;

  .option-value {
    font-weight: 700;
  }
}
</style>
